import { html, nothing, type TemplateResult } from "lit";

// ---------------------------------------------------------------------------
// Landing page for alitagpt.com — unauthenticated users
// ---------------------------------------------------------------------------
//
// Claude-style chat prompt + scrolling marquee of 100+ real activity cards
// showing what ALITA can do across all Composio-supported apps.
// ---------------------------------------------------------------------------

// Callback passed from login-gate.ts — triggers Clerk sign-in flow
let _onGetStarted: (() => void) | null = null;

// ---------------------------------------------------------------------------
// App logos — official icons for showcase cards
// ---------------------------------------------------------------------------

// Simple Icons CDN — fast, globally distributed, instant SVG loading
const CDN = "https://cdn.simpleicons.org";
export const APP_LOGOS: Record<string, string> = {
  gmail: `${CDN}/gmail/EA4335`,
  googlecalendar: `${CDN}/googlecalendar/4285F4`,
  googledocs: `${CDN}/googledocs/4285F4`,
  googlesheets: `${CDN}/googlesheets/34A853`,
  googledrive: `${CDN}/googledrive/4285F4`,
  slack: `${CDN}/slack/4A154B`,
  github: `${CDN}/github/181717`,
  notion: `${CDN}/notion/000000`,
  linear: `${CDN}/linear/5E6AD2`,
  linkedin: `${CDN}/linkedin/0A66C2`,
  hubspot: `${CDN}/hubspot/FF7A59`,
  trello: `${CDN}/trello/0052CC`,
  jira: `${CDN}/jira/0052CC`,
  asana: `${CDN}/asana/F06A6A`,
  discord: `${CDN}/discord/5865F2`,
  twitter: `${CDN}/x/000000`,
  telegram: `${CDN}/telegram/26A5E4`,
  zoom: `${CDN}/zoom/0B5CFF`,
  stripe: `${CDN}/stripe/635BFF`,
  shopify: `${CDN}/shopify/7AB55C`,
  youtube: `${CDN}/youtube/FF0000`,
  figma: `${CDN}/figma/F24E1E`,
  dropbox: `${CDN}/dropbox/0061FF`,
  outlook: `${CDN}/microsoftoutlook/0078D4`,
  teams: `${CDN}/microsoftteams/6264A7`,
  instagram: `${CDN}/instagram/E4405F`,
  airtable: `${CDN}/airtable/18BFFF`,
  zendesk: `${CDN}/zendesk/03363D`,
  intercom: `${CDN}/intercom/6AFDEF`,
  mailchimp: `${CDN}/mailchimp/FFE01B`,
  salesforce: `${CDN}/salesforce/00A1E0`,
  twilio: `${CDN}/twilio/F22F46`,
  calendly: `${CDN}/calendly/006BFF`,
};

// ---------------------------------------------------------------------------
// 100+ showcase activity cards — realistic actions per app
// ---------------------------------------------------------------------------

export interface ShowcaseCard {
  app: string;
  appLabel: string;
  actionType: string;
  detail: string;
  time: string;
}

export const SHOWCASE_CARDS: ShowcaseCard[] = [
  // ── Gmail (Send, Reply, Forward, Draft) ──
  { app: "gmail", appLabel: "Gmail", actionType: "Email Sent", detail: "To: sarah@company.com — Q2 Report Key Takeaways", time: "2m ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Email Replied", detail: "Re: Partnership Proposal — Sounds great, let's schedule a call", time: "8m ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Email Forwarded", detail: "Fwd: Invoice #4021 → accounting@company.com", time: "25m ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Draft Created", detail: "Subject: Follow-up on Demo — Hi Mark, thanks for your time...", time: "45m ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Email Sent", detail: "To: investors@vc.com — Monthly Update March 2026", time: "1h ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Email Replied", detail: "Re: Job Application — We'd love to schedule an interview", time: "2h ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Email Sent", detail: "To: team@startup.io — Product launch timeline update", time: "4h ago" },
  { app: "gmail", appLabel: "Gmail", actionType: "Draft Created", detail: "Subject: Board Meeting Agenda — Q1 review and roadmap", time: "6h ago" },

  // ── Google Calendar (Create, Update, Delete events) ──
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Created", detail: "Weekly Team Standup — Mon 9:00 AM with team@company.com", time: "1h ago" },
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Updated", detail: "1:1 with Sarah moved to Thursday 2:00 PM", time: "2h ago" },
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Created", detail: "Product Demo — Wed 11:00 AM with client@enterprise.com", time: "3h ago" },
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Deleted", detail: "Cancelled: Budget Review (rescheduled to next week)", time: "5h ago" },
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Created", detail: "Sprint Planning — Mon 10:30 AM, Engineering team", time: "8h ago" },
  { app: "googlecalendar", appLabel: "Google Calendar", actionType: "Event Updated", detail: "All-Hands Meeting — added Zoom link and agenda", time: "1d ago" },

  // ── Slack (Messages, Channels, Topics) ──
  { app: "slack", appLabel: "Slack", actionType: "Message Sent", detail: "#engineering — Deploy v2.3 completed, all health checks passing", time: "5m ago" },
  { app: "slack", appLabel: "Slack", actionType: "Message Sent", detail: "#general — Reminder: Company offsite next Friday!", time: "15m ago" },
  { app: "slack", appLabel: "Slack", actionType: "Channel Created", detail: "#project-phoenix — New channel for the rebrand initiative", time: "1h ago" },
  { app: "slack", appLabel: "Slack", actionType: "Topic Updated", detail: "#sales — Updated topic: Q2 target $2.5M ARR", time: "3h ago" },
  { app: "slack", appLabel: "Slack", actionType: "Message Sent", detail: "DM to @mike — Hey, PR is ready for review", time: "30m ago" },
  { app: "slack", appLabel: "Slack", actionType: "Message Sent", detail: "#product — New feature spec uploaded to Notion", time: "2h ago" },
  { app: "slack", appLabel: "Slack", actionType: "Message Sent", detail: "#customer-support — Resolved ticket #892 for Acme Corp", time: "4h ago" },

  // ── GitHub (PRs, Issues, Comments, Branches, Merge) ──
  { app: "github", appLabel: "GitHub", actionType: "PR Created", detail: "Fix auth redirect on mobile Safari — acme/frontend", time: "10m ago" },
  { app: "github", appLabel: "GitHub", actionType: "Issue Created", detail: "Mobile nav not responsive on iPad — acme/frontend", time: "30m ago" },
  { app: "github", appLabel: "GitHub", actionType: "PR Merged", detail: "feat: Add Stripe webhook handler — acme/backend #142", time: "1h ago" },
  { app: "github", appLabel: "GitHub", actionType: "Comment Added", detail: "PR #87: LGTM, just one nit on the error handling", time: "2h ago" },
  { app: "github", appLabel: "GitHub", actionType: "Branch Created", detail: "feature/onboarding-flow from main — acme/frontend", time: "3h ago" },
  { app: "github", appLabel: "GitHub", actionType: "Issue Created", detail: "API rate limiting returns 500 instead of 429 — acme/api", time: "5h ago" },
  { app: "github", appLabel: "GitHub", actionType: "PR Created", detail: "refactor: Migrate auth to NextAuth v5 — acme/web", time: "8h ago" },
  { app: "github", appLabel: "GitHub", actionType: "PR Merged", detail: "fix: Memory leak in WebSocket handler — acme/server #98", time: "1d ago" },

  // ── Notion (Pages, Databases) ──
  { app: "notion", appLabel: "Notion", actionType: "Page Created", detail: "Sprint 14 Retrospective Notes", time: "20m ago" },
  { app: "notion", appLabel: "Notion", actionType: "Page Updated", detail: "Product Roadmap Q2 — Added AI features section", time: "1h ago" },
  { app: "notion", appLabel: "Notion", actionType: "Database Created", detail: "Customer Feedback Tracker — 12 properties configured", time: "3h ago" },
  { app: "notion", appLabel: "Notion", actionType: "Page Created", detail: "Meeting Notes: Investor Call with Sequoia", time: "5h ago" },
  { app: "notion", appLabel: "Notion", actionType: "Page Updated", detail: "Engineering Wiki — Updated deployment guide", time: "8h ago" },
  { app: "notion", appLabel: "Notion", actionType: "Page Created", detail: "Competitive Analysis: AI Assistants Market 2026", time: "1d ago" },

  // ── Linear (Issues, Projects, Cycles) ──
  { app: "linear", appLabel: "Linear", actionType: "Issue Created", detail: "Mobile nav not responsive on iPad — Frontend team", time: "15m ago" },
  { app: "linear", appLabel: "Linear", actionType: "Issue Updated", detail: "Auth flow broken on Safari — moved to In Progress", time: "1h ago" },
  { app: "linear", appLabel: "Linear", actionType: "Project Created", detail: "Q2 Performance Optimization — 8 issues planned", time: "4h ago" },
  { app: "linear", appLabel: "Linear", actionType: "Issue Created", detail: "Add dark mode support to settings page — Design team", time: "6h ago" },
  { app: "linear", appLabel: "Linear", actionType: "Issue Completed", detail: "Implement SSO login for enterprise — Backend team", time: "1d ago" },

  // ── LinkedIn (Posts, Messages, Connections) ──
  { app: "linkedin", appLabel: "LinkedIn", actionType: "Post Published", detail: "5 ways AI is changing how founders run their startups", time: "30m ago" },
  { app: "linkedin", appLabel: "LinkedIn", actionType: "Message Sent", detail: "To: Jane Smith — Great connecting at the conference!", time: "2h ago" },
  { app: "linkedin", appLabel: "LinkedIn", actionType: "Post Published", detail: "We just raised our Series A! Excited to share the news...", time: "1d ago" },
  { app: "linkedin", appLabel: "LinkedIn", actionType: "Post Published", detail: "Lessons from scaling a team from 5 to 50 in 12 months", time: "2d ago" },
  { app: "linkedin", appLabel: "LinkedIn", actionType: "Message Sent", detail: "To: recruiter@bigtech.com — Thanks for the opportunity", time: "3d ago" },

  // ── HubSpot (Contacts, Deals, Companies) ──
  { app: "hubspot", appLabel: "HubSpot", actionType: "Contact Created", detail: "John Chen — VP Engineering at TechCorp", time: "20m ago" },
  { app: "hubspot", appLabel: "HubSpot", actionType: "Deal Created", detail: "Enterprise Plan — TechCorp — $48,000/yr", time: "1h ago" },
  { app: "hubspot", appLabel: "HubSpot", actionType: "Deal Updated", detail: "Acme Corp deal moved to Closed Won — $24,000", time: "3h ago" },
  { app: "hubspot", appLabel: "HubSpot", actionType: "Contact Updated", detail: "Sarah Miller — Added phone and company info", time: "6h ago" },
  { app: "hubspot", appLabel: "HubSpot", actionType: "Company Created", detail: "NovaTech Inc — SaaS, 50-200 employees", time: "1d ago" },

  // ── Trello (Cards, Lists, Boards) ──
  { app: "trello", appLabel: "Trello", actionType: "Card Created", detail: "Design new onboarding flow — UX Sprint board", time: "10m ago" },
  { app: "trello", appLabel: "Trello", actionType: "Card Moved", detail: "API integration moved to Done — Backend board", time: "45m ago" },
  { app: "trello", appLabel: "Trello", actionType: "Board Created", detail: "Q2 Marketing Campaigns — 5 lists created", time: "3h ago" },
  { app: "trello", appLabel: "Trello", actionType: "Card Created", detail: "Write blog post: AI trends 2026 — Content board", time: "5h ago" },

  // ── Jira (Issues, Sprints, Transitions) ──
  { app: "jira", appLabel: "Jira", actionType: "Issue Created", detail: "PROJ-421: Payment gateway timeout on large orders", time: "12m ago" },
  { app: "jira", appLabel: "Jira", actionType: "Issue Transitioned", detail: "PROJ-398: Login page redesign → In Review", time: "1h ago" },
  { app: "jira", appLabel: "Jira", actionType: "Issue Created", detail: "PROJ-422: Add unit tests for user service", time: "4h ago" },
  { app: "jira", appLabel: "Jira", actionType: "Issue Updated", detail: "PROJ-405: Priority changed to Critical, assigned to devops", time: "8h ago" },

  // ── Google Docs (Create, Update) ──
  { app: "googledocs", appLabel: "Google Docs", actionType: "Document Created", detail: "Q2 Business Review — Executive summary template", time: "35m ago" },
  { app: "googledocs", appLabel: "Google Docs", actionType: "Document Updated", detail: "Product Requirements Doc — Added user stories section", time: "2h ago" },
  { app: "googledocs", appLabel: "Google Docs", actionType: "Document Created", detail: "Interview Debrief: Senior Engineer candidate", time: "6h ago" },

  // ── Google Sheets (Create, Update cells) ──
  { app: "googlesheets", appLabel: "Google Sheets", actionType: "Spreadsheet Created", detail: "Revenue Forecast Q2 2026 — Monthly projections", time: "40m ago" },
  { app: "googlesheets", appLabel: "Google Sheets", actionType: "Cells Updated", detail: "Sales Pipeline — Updated March close rates", time: "3h ago" },
  { app: "googlesheets", appLabel: "Google Sheets", actionType: "Spreadsheet Created", detail: "Hiring Tracker — Engineering team openings", time: "1d ago" },

  // ── Discord (Messages, Channels) ──
  { app: "discord", appLabel: "Discord", actionType: "Message Sent", detail: "#announcements — New release v3.0 is live! Check it out", time: "5m ago" },
  { app: "discord", appLabel: "Discord", actionType: "Channel Created", detail: "#beta-testers — Private channel for early access users", time: "2h ago" },
  { app: "discord", appLabel: "Discord", actionType: "Message Sent", detail: "#dev-chat — Deployed hotfix for the webhook issue", time: "4h ago" },

  // ── Twitter/X (Posts, DMs) ──
  { app: "twitter", appLabel: "X (Twitter)", actionType: "Post Published", detail: "Just shipped our biggest update yet. AI that actually works.", time: "15m ago" },
  { app: "twitter", appLabel: "X (Twitter)", actionType: "Post Published", detail: "Thread: 10 lessons from our first year of building in public", time: "3h ago" },
  { app: "twitter", appLabel: "X (Twitter)", actionType: "DM Sent", detail: "To: @techwriter — Would love to chat about a feature", time: "1d ago" },

  // ── Telegram (Messages, Groups) ──
  { app: "telegram", appLabel: "Telegram", actionType: "Message Sent", detail: "DevOps Group — Server migration completed successfully", time: "8m ago" },
  { app: "telegram", appLabel: "Telegram", actionType: "Message Sent", detail: "Founders Chat — Updated cap table shared in Drive", time: "2h ago" },

  // ── Zoom (Meetings) ──
  { app: "zoom", appLabel: "Zoom", actionType: "Meeting Created", detail: "Product Demo with Acme Corp — Thu 3:00 PM EST", time: "30m ago" },
  { app: "zoom", appLabel: "Zoom", actionType: "Meeting Created", detail: "Investor Update Call — Fri 11:00 AM, recurring weekly", time: "4h ago" },

  // ── Stripe (Invoices, Payment links) ──
  { app: "stripe", appLabel: "Stripe", actionType: "Invoice Created", detail: "Invoice #1087 — $2,400 to Acme Corp for Pro Plan", time: "20m ago" },
  { app: "stripe", appLabel: "Stripe", actionType: "Payment Link Created", detail: "Enterprise Onboarding — $499/mo subscription link", time: "3h ago" },
  { app: "stripe", appLabel: "Stripe", actionType: "Invoice Sent", detail: "Invoice #1085 — $1,200 to NovaTech for Starter Plan", time: "1d ago" },

  // ── Shopify (Products, Orders) ──
  { app: "shopify", appLabel: "Shopify", actionType: "Product Created", detail: "Premium Wireless Earbuds — $79.99, 500 units in stock", time: "45m ago" },
  { app: "shopify", appLabel: "Shopify", actionType: "Order Updated", detail: "Order #8834 — Marked as shipped, tracking added", time: "2h ago" },
  { app: "shopify", appLabel: "Shopify", actionType: "Product Updated", detail: "Smart Watch Pro — Price updated to $199.99", time: "6h ago" },

  // ── YouTube (Upload, Update) ──
  { app: "youtube", appLabel: "YouTube", actionType: "Video Uploaded", detail: "How We Built Our AI Agent in 30 Days — 12:34 min", time: "1h ago" },
  { app: "youtube", appLabel: "YouTube", actionType: "Video Updated", detail: "Updated thumbnail and tags for Product Demo video", time: "5h ago" },

  // ── Instagram (Posts) ──
  { app: "instagram", appLabel: "Instagram", actionType: "Post Published", detail: "Behind the scenes at our new office — carousel post", time: "2h ago" },
  { app: "instagram", appLabel: "Instagram", actionType: "Story Published", detail: "Live Q&A announcement — Swipe up for details", time: "6h ago" },

  // ── Figma (Comments) ──
  { app: "figma", appLabel: "Figma", actionType: "Comment Added", detail: "Dashboard redesign — The spacing on cards needs adjustment", time: "25m ago" },
  { app: "figma", appLabel: "Figma", actionType: "Comment Added", detail: "Mobile app mockup — Love the new nav, ship it!", time: "3h ago" },

  // ── Asana (Tasks, Projects) ──
  { app: "asana", appLabel: "Asana", actionType: "Task Created", detail: "Write Q2 blog content calendar — Marketing project", time: "18m ago" },
  { app: "asana", appLabel: "Asana", actionType: "Task Completed", detail: "Launch landing page A/B test — Growth project", time: "2h ago" },
  { app: "asana", appLabel: "Asana", actionType: "Project Created", detail: "Website Redesign 2026 — 24 tasks, 4 milestones", time: "1d ago" },

  // ── Dropbox (Upload, Share) ──
  { app: "dropbox", appLabel: "Dropbox", actionType: "File Uploaded", detail: "Q2-Financial-Report.pdf — 2.4 MB to /Reports", time: "35m ago" },
  { app: "dropbox", appLabel: "Dropbox", actionType: "Folder Shared", detail: "Brand Assets shared with design@agency.com", time: "4h ago" },

  // ── Outlook (Email) ──
  { app: "outlook", appLabel: "Outlook", actionType: "Email Sent", detail: "To: board@company.com — Board meeting materials attached", time: "50m ago" },
  { app: "outlook", appLabel: "Outlook", actionType: "Email Sent", detail: "To: hr@company.com — Updated benefits enrollment form", time: "5h ago" },

  // ── Microsoft Teams (Messages) ──
  { app: "teams", appLabel: "MS Teams", actionType: "Message Sent", detail: "Engineering channel — Sprint velocity report attached", time: "22m ago" },
  { app: "teams", appLabel: "MS Teams", actionType: "Meeting Created", detail: "Cross-team sync — Tue 2:00 PM with product & design", time: "3h ago" },

  // ── Airtable (Records) ──
  { app: "airtable", appLabel: "Airtable", actionType: "Record Created", detail: "Content Calendar — Blog post: AI Agent Best Practices", time: "28m ago" },
  { app: "airtable", appLabel: "Airtable", actionType: "Record Updated", detail: "CRM Base — Acme Corp status changed to Active", time: "2h ago" },

  // ── Zendesk (Tickets) ──
  { app: "zendesk", appLabel: "Zendesk", actionType: "Ticket Created", detail: "#4521: Login issue on mobile — Priority: High", time: "7m ago" },
  { app: "zendesk", appLabel: "Zendesk", actionType: "Ticket Resolved", detail: "#4518: Billing discrepancy — Refund processed", time: "1h ago" },

  // ── Intercom (Messages, Conversations) ──
  { app: "intercom", appLabel: "Intercom", actionType: "Message Sent", detail: "To: Acme Corp — Your integration is now live!", time: "12m ago" },
  { app: "intercom", appLabel: "Intercom", actionType: "Conversation Closed", detail: "Ticket #892 — Issue resolved, customer satisfied", time: "3h ago" },

  // ── Mailchimp (Campaigns, Contacts) ──
  { app: "mailchimp", appLabel: "Mailchimp", actionType: "Campaign Sent", detail: "April Newsletter — 12,400 recipients", time: "40m ago" },
  { app: "mailchimp", appLabel: "Mailchimp", actionType: "Contact Added", detail: "john@enterprise.com — Enterprise segment", time: "4h ago" },

  // ── Salesforce (Leads, Opportunities) ──
  { app: "salesforce", appLabel: "Salesforce", actionType: "Lead Created", detail: "Maria Garcia — CTO at FinanceApp, inbound from webinar", time: "18m ago" },
  { app: "salesforce", appLabel: "Salesforce", actionType: "Opportunity Updated", detail: "BigCo Enterprise — Stage: Negotiation, $120K ARR", time: "2h ago" },

  // ── Twilio (SMS) ──
  { app: "twilio", appLabel: "Twilio", actionType: "SMS Sent", detail: "To: +1-555-0123 — Your verification code is 847291", time: "3m ago" },
  { app: "twilio", appLabel: "Twilio", actionType: "SMS Sent", detail: "To: +1-555-0456 — Order #8834 has been shipped!", time: "1h ago" },

  // ── Calendly (Events) ──
  { app: "calendly", appLabel: "Calendly", actionType: "Event Type Created", detail: "30-min Product Demo — Available Mon-Fri 10 AM-4 PM", time: "2h ago" },
  { app: "calendly", appLabel: "Calendly", actionType: "Event Scheduled", detail: "Demo with Sarah Chen — Thu 2:30 PM EST", time: "4h ago" },

  // ── Google Drive (Upload, Share) ──
  { app: "googledrive", appLabel: "Google Drive", actionType: "File Uploaded", detail: "Pitch-Deck-v3.pdf uploaded to /Fundraising", time: "55m ago" },
  { app: "googledrive", appLabel: "Google Drive", actionType: "File Shared", detail: "Design specs shared with eng-team@company.com", time: "3h ago" },
];

// ---------------------------------------------------------------------------
// Split cards into rows for the marquee
// ---------------------------------------------------------------------------

const ROW_SIZE = Math.ceil(SHOWCASE_CARDS.length / 4);
const ROW1 = SHOWCASE_CARDS.slice(0, ROW_SIZE);
const ROW2 = SHOWCASE_CARDS.slice(ROW_SIZE, ROW_SIZE * 2);
const ROW3 = SHOWCASE_CARDS.slice(ROW_SIZE * 2, ROW_SIZE * 3);
const ROW4 = SHOWCASE_CARDS.slice(ROW_SIZE * 3);

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let inputValue = "";

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

export function renderAppLogo(app: string, size = 28): TemplateResult {
  const url = APP_LOGOS[app];
  if (url) {
    return html`<img
      src=${url}
      alt=${app}
      width=${size}
      height=${size}
      style="border-radius: 8px; object-fit: contain; flex-shrink: 0;"
      loading="lazy"
    />`;
  }
  return html`<div style="
    width: ${size}px; height: ${size}px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    background: var(--l-muted); font-size: ${size * 0.5}px; flex-shrink: 0;
  ">&#9889;</div>`;
}

function renderMarqueeCard(card: ShowcaseCard): TemplateResult {
  return html`
    <div class="marquee-card">
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:8px;">
        ${renderAppLogo(card.app, 28)}
        <div style="min-width:0;">
          <div style="font-size:13px; font-weight:600; color:var(--l-text-strong); white-space:nowrap;">${card.actionType}</div>
          <div style="font-size:10px; color:var(--l-text); opacity:0.5;">${card.appLabel} · ${card.time}</div>
        </div>
        <span class="marquee-badge">
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
          Success
        </span>
      </div>
      <div style="font-size:11px; color:var(--l-text); line-height:1.45; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;">${card.detail}</div>
    </div>
  `;
}

function renderMarqueeRow(cards: ShowcaseCard[], reverse: boolean, speed: number): TemplateResult {
  // Double for seamless loop
  return html`
    <div class="marquee-track">
      <div class="marquee-fade-left"></div>
      <div class="marquee-scroll" style="animation-duration:${speed}s; animation-direction:${reverse ? "reverse" : "normal"};">
        ${cards.map(c => renderMarqueeCard(c))}
        ${cards.map(c => renderMarqueeCard(c))}
      </div>
      <div class="marquee-fade-right"></div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const plusIcon = html`<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;

const thinkingIcon = html`<svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M10.3857 2.50977C14.3486 2.71054 17.5 5.98724 17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 9.72386 2.72386 9.5 3 9.5C3.27614 9.5 3.5 9.72386 3.5 10C3.5 13.5899 6.41015 16.5 10 16.5C13.5899 16.5 16.5 13.5899 16.5 10C16.5 6.5225 13.7691 3.68312 10.335 3.50879L10 3.5L9.89941 3.49023C9.67145 3.44371 9.5 3.24171 9.5 3C9.5 2.72386 9.72386 2.5 10 2.5L10.3857 2.50977ZM10 5.5C10.2761 5.5 10.5 5.72386 10.5 6V9.69043L13.2236 11.0527C13.4706 11.1762 13.5708 11.4766 13.4473 11.7236C13.3392 11.9397 13.0957 12.0435 12.8711 11.9834L12.7764 11.9473L9.77637 10.4473C9.60698 10.3626 9.5 10.1894 9.5 10V6C9.5 5.72386 9.72386 5.5 10 5.5ZM3.66211 6.94141C4.0273 6.94159 4.32303 7.23735 4.32324 7.60254C4.32324 7.96791 4.02743 8.26446 3.66211 8.26465C3.29663 8.26465 3 7.96802 3 7.60254C3.00021 7.23723 3.29676 6.94141 3.66211 6.94141ZM4.95605 4.29395C5.32146 4.29404 5.61719 4.59063 5.61719 4.95605C5.6171 5.3214 5.3214 5.61709 4.95605 5.61719C4.59063 5.61719 4.29403 5.32146 4.29395 4.95605C4.29395 4.59057 4.59057 4.29395 4.95605 4.29395ZM7.60254 3C7.96802 3 8.26465 3.29663 8.26465 3.66211C8.26446 4.02743 7.96791 4.32324 7.60254 4.32324C7.23736 4.32302 6.94159 4.0273 6.94141 3.66211C6.94141 3.29676 7.23724 3.00022 7.60254 3Z"></path></svg>`;

const arrowUpIcon = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;

const chevronDownIcon = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

// ALITA logo — served from same CDN edge as the page itself
const alitaLogo = html`
  <img src="/alita-logo.png" alt="ALITA" width="56" height="56" style="margin:0 auto; display:block; border-radius:14px;" />
`;

// ---------------------------------------------------------------------------
// Main landing render
// ---------------------------------------------------------------------------

export function renderLanding(onGetStarted?: () => void): TemplateResult {
  if (onGetStarted) _onGetStarted = onGetStarted;

  const handleSubmit = () => {
    if (_onGetStarted) _onGetStarted();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return html`
    <style>
      /* ── Marquee animation ── */
      @keyframes marqueeScroll {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-50%); }
      }

      /* ── Reset focus outlines inside landing ── */
      .landing-root *:focus,
      .landing-root *:focus-visible {
        outline: none !important;
        box-shadow: none !important;
        outline-offset: 0 !important;
      }
      .landing-root button:focus-visible {
        outline: 2px solid var(--l-accent) !important;
        outline-offset: 2px !important;
      }

      /* ── Warm light palette ── */
      .landing-root {
        --l-bg: #FAF9F5;
        --l-card: #ffffff;
        --l-border: #E5E2DC;
        --l-text: #73726C;
        --l-text-strong: #1F1E1D;
        --l-text-muted: #888888;
        --l-accent: #D97757;
        --l-accent-hover: #C6613F;
        --l-success: #16a34a;
        --l-muted: #F0EEE6;
        --l-input-bg: #ffffff;
        --l-input-border: #DDDDDD;

        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        background: var(--l-bg);
        padding: 0;
        overflow-x: hidden;
        overflow-y: auto;
        color: var(--l-text);
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
        -webkit-font-smoothing: antialiased;
      }

      /* ── Hero ── */
      .landing-hero {
        max-width: 640px;
        width: 100%;
        text-align: center;
        padding: clamp(48px, 10vh, 100px) 16px 36px;
      }

      /* ── Claude-style prompt ── */
      .landing-prompt {
        position: relative;
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        border-radius: 16px;
        border: 1px solid var(--l-input-border);
        background: var(--l-input-bg);
        box-shadow: 0 0 15px rgba(0,0,0,0.06);
        transition: border-color 0.2s, box-shadow 0.2s;
        cursor: text;
      }
      .landing-prompt:hover { box-shadow: 0 0 20px rgba(0,0,0,0.09); }
      .landing-prompt:focus-within { box-shadow: 0 0 25px rgba(0,0,0,0.12); border-color: #ccc; }
      .landing-prompt-inner { display:flex; flex-direction:column; padding:12px 12px 8px; gap:8px; }
      .landing-prompt textarea {
        width:100%; border:none !important; background:transparent; color:var(--l-text-strong);
        font-size:16px; line-height:1.6; resize:none; outline:none !important;
        min-height:40px; max-height:120px; font-family:inherit; padding:0 4px;
        -webkit-font-smoothing:antialiased;
        box-shadow:none !important;
        -webkit-appearance:none;
      }
      .landing-prompt textarea:focus,
      .landing-prompt textarea:focus-visible,
      .landing-prompt textarea:active {
        border:none !important;
        outline:none !important;
        box-shadow:none !important;
        ring:none !important;
      }
      .landing-prompt textarea::placeholder { color:var(--l-text-muted); }
      .landing-prompt-actions { display:flex; align-items:center; justify-content:space-between; gap:8px; }
      .landing-prompt-actions-left { display:flex; align-items:center; gap:2px; }
      .landing-prompt-actions-right { display:flex; align-items:center; gap:4px; }
      .landing-icon-btn {
        display:inline-flex; align-items:center; justify-content:center;
        width:32px; height:32px; border-radius:8px; border:none;
        background:transparent; color:var(--l-text-muted); cursor:pointer;
        transition:color 0.15s, background 0.15s; flex-shrink:0;
      }
      .landing-icon-btn:hover { color:var(--l-text-strong); background:var(--l-muted); }
      .landing-model-selector {
        display:inline-flex; align-items:center; gap:3px; height:32px;
        padding:0 8px 0 10px; border-radius:12px; border:none;
        background:transparent; color:var(--l-text); font-size:14px;
        font-weight:500; cursor:pointer; transition:color 0.15s, background 0.15s;
        font-family:inherit; white-space:nowrap;
      }
      .landing-model-selector:hover { color:var(--l-text-strong); background:var(--l-muted); }
      .landing-model-selector svg { opacity:0.6; }
      .landing-send-btn {
        display:inline-flex; align-items:center; justify-content:center;
        width:32px; height:32px; border-radius:12px; border:none;
        background:var(--l-accent); color:white; cursor:pointer;
        transition:background 0.15s, transform 0.1s; flex-shrink:0;
        box-shadow:0 2px 8px rgba(217,119,87,0.3);
      }
      .landing-send-btn:hover { background:var(--l-accent-hover); transform:scale(1.02); }
      .landing-send-btn:active { transform:scale(0.95); }

      /* ── Quick action pills ── */
      .landing-quick-actions {
        display:flex; flex-wrap:wrap; justify-content:center; gap:8px;
        max-width:600px; margin:0 auto; padding:16px 0 0;
      }
      .landing-quick-pill {
        display:inline-flex; align-items:center; gap:6px; padding:6px 14px;
        border-radius:9999px; border:1px solid var(--l-input-border);
        background:transparent; color:var(--l-text); font-size:13px;
        font-family:inherit; cursor:pointer; transition:background 0.15s, color 0.15s;
      }
      .landing-quick-pill:hover { background:var(--l-muted); color:var(--l-text-strong); }
      .landing-quick-pill svg { width:16px; height:16px; opacity:0.6; }
      .landing-disclaimer { text-align:center; margin-top:12px; font-size:11px; color:var(--l-text-muted); opacity:0.6; }

      /* ── Marquee ── */
      .marquee-section {
        width:100%; padding:0 0 20px; overflow:hidden;
      }
      .marquee-section-header {
        text-align:center; margin-bottom:20px; padding:0 16px;
      }
      .marquee-track {
        position:relative; width:100%; overflow:hidden; margin-bottom:12px;
      }
      .marquee-scroll {
        display:flex; gap:12px; width:max-content;
        animation: marqueeScroll 80s linear infinite;
      }
      .marquee-track:hover .marquee-scroll {
        animation-play-state: paused;
      }
      .marquee-fade-left {
        pointer-events:none; position:absolute; left:0; top:0; height:100%;
        width:80px; z-index:2;
        background:linear-gradient(to right, var(--l-bg), transparent);
      }
      .marquee-fade-right {
        pointer-events:none; position:absolute; right:0; top:0; height:100%;
        width:80px; z-index:2;
        background:linear-gradient(to left, var(--l-bg), transparent);
      }
      .marquee-card {
        flex-shrink:0; width:300px; padding:14px 16px;
        border-radius:14px; border:1px solid var(--l-border);
        background:var(--l-card); transition:box-shadow 0.2s, transform 0.2s;
      }
      .marquee-card:hover {
        box-shadow:0 4px 16px rgba(0,0,0,0.06);
        transform:translateY(-1px);
      }
      .marquee-badge {
        margin-left:auto; flex-shrink:0;
        display:inline-flex; align-items:center; gap:3px;
        padding:2px 7px; border-radius:9999px; font-size:9px; font-weight:600;
        background:color-mix(in srgb, var(--l-success) 12%, transparent);
        color:var(--l-success); white-space:nowrap;
      }

      /* ── CTA ── */
      .landing-cta-section { max-width:480px; width:100%; text-align:center; padding:32px 16px 60px; }
      .landing-cta-btn {
        display:inline-flex; align-items:center; justify-content:center; gap:8px;
        padding:14px 32px; border-radius:14px; border:none;
        background:var(--l-accent); color:white; font-size:15px; font-weight:600;
        cursor:pointer; transition:transform 0.15s, box-shadow 0.15s;
        box-shadow:0 4px 16px rgba(217,119,87,0.25); text-decoration:none; font-family:inherit;
      }
      .landing-cta-btn:hover { transform:translateY(-1px); box-shadow:0 6px 24px rgba(217,119,87,0.35); }

      @media (max-width: 640px) {
        .marquee-card { width:260px; }
        .landing-quick-actions { gap:6px; }
        .landing-quick-pill { font-size:12px; padding:5px 10px; }
      }
    </style>

    <div class="landing-root">
      <!-- Hero -->
      <div class="landing-hero">
        <div style="margin-bottom:16px;">${alitaLogo}</div>
        <h1 style="margin:0; font-size:clamp(28px,5vw,38px); font-weight:300; color:var(--l-text-strong); line-height:1.15; letter-spacing:-0.02em; font-family:'Source Serif 4',Georgia,serif;">
          Ready to assist you
        </h1>
        <p style="margin:8px 0 0; font-size:15px; color:var(--l-text); opacity:0.7; line-height:1.5;">
          ALITA sends emails, manages your calendar, posts to social media, creates issues — all on autopilot.
        </p>

        <!-- Claude-style prompt -->
        <div class="landing-prompt" style="margin-top:28px;">
          <div class="landing-prompt-inner">
            <textarea rows="1" placeholder="How can I help you today?"
              .value=${inputValue}
              @input=${(e: Event) => { inputValue = (e.target as HTMLTextAreaElement).value; }}
              @keydown=${handleKeyDown}
            ></textarea>
            <div class="landing-prompt-actions">
              <div class="landing-prompt-actions-left">
                <button class="landing-icon-btn" title="Attach files" @click=${handleSubmit}>${plusIcon}</button>
                <button class="landing-icon-btn" title="Extended thinking" @click=${handleSubmit}>${thinkingIcon}</button>
              </div>
              <div class="landing-prompt-actions-right">
                <button class="landing-model-selector" @click=${handleSubmit}>ALITA ${chevronDownIcon}</button>
                <button class="landing-send-btn" @click=${handleSubmit} title="Send message">${arrowUpIcon}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick pills -->
        <div class="landing-quick-actions">
          <button class="landing-quick-pill" @click=${handleSubmit}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            Draft an email
          </button>
          <button class="landing-quick-pill" @click=${handleSubmit}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Schedule a meeting
          </button>
          <button class="landing-quick-pill" @click=${handleSubmit}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
            Create a PR
          </button>
          <button class="landing-quick-pill" @click=${handleSubmit}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Post to Slack
          </button>
        </div>
        <div class="landing-disclaimer">AI can make mistakes. Please check important information.</div>
      </div>

      <!-- Scrolling Activity Marquee -->
      <div class="marquee-section">
        <div class="marquee-section-header">
          <h2 style="margin:0; font-size:20px; font-weight:700; color:var(--l-text-strong);">
            See what ALITA users are doing
          </h2>
          <p style="margin:6px 0 0; font-size:13px; color:var(--l-text); opacity:0.45;">
            Real actions across 30+ apps — emails, PRs, meetings, invoices, posts, and more
          </p>
        </div>
        ${renderMarqueeRow(ROW1, false, 90)}
        ${renderMarqueeRow(ROW2, true, 85)}
        ${renderMarqueeRow(ROW3, false, 95)}
        ${renderMarqueeRow(ROW4, true, 80)}
      </div>

      <!-- CTA -->
      <div class="landing-cta-section">
        <button class="landing-cta-btn" @click=${handleSubmit} style="border:none; cursor:pointer;">
          Get started free
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
        <div style="margin-top:14px;">
          <button @click=${handleSubmit} style="font-size:13px; color:var(--l-text); opacity:0.5; text-decoration:none; font-family:inherit; background:none; border:none; cursor:pointer;">
            Already have an account? <span style="text-decoration:underline; opacity:1;">Sign in</span>
          </button>
        </div>
      </div>
    </div>
  `;
}
